import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";
import { ISession } from "../interfaces/Session";

interface IFetchSession {
    programId: string;
}

interface IStoreSessions {
    programId: string
    sessions: ISession[]
}

interface IDeleteSession {
    sessionId: string
    programId: string
}

interface ProgramSessionsState {
    loading: boolean;
    error: string | null;
    programSessions: ISession[] | [];
}

const initialState: ProgramSessionsState = {
    loading: false,
    error: null,
    programSessions: [],
};

/**
 * Axios request to fetch sessions for a given programId
 */
export const fetchSessions = createAsyncThunk('sessions/fetchSessions',
    async ({ programId }: IFetchSession, thunkAPI) => {
        try {
            const accessToken = JSON.parse(localStorage.getItem('loginData') as string)?.accessToken;

            if (!accessToken) {
                console.error('Access token not found in the response.');
                return {}; // or any default value if needed
            }
            const response = await axios(`${BASE_URL}/api/v1/programs/${programId}/sessions`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    // Add other headers if needed
                },
            });
            const responseData = response?.data
            if (response.status !== 200 && response.statusText !== 'OK') {
                throw new Error(`Request failed with status ${response?.data?.message}`);
            }
            return responseData?.data?.sessions || [];
        } catch (error: any) {
            console.error('Error:', error.message);
            return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
        }
    }
);

/**
 * AXIOS request for store sessions
 */
export const storeSessions = createAsyncThunk('sessions/storeSessions',
    async ({ programId, sessions }: IStoreSessions, thunkAPI) => {
        try {
            const accessToken = JSON.parse(localStorage.getItem('loginData') as string)?.accessToken;

            if (!accessToken) {
                console.error('Access token not found in the response.');
                return {}; // or any default value if needed
            }
            const response = await axios.post(`${BASE_URL}/api/v1/sessions`, {
                programId,
                sessions
            }, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    // Add other headers if needed
                },
            });
            const responseData = response?.data
            if (response.status !== 200 && response.statusText !== 'OK') {
                throw new Error(`Request failed with status ${response?.data?.message}`);
            }
            thunkAPI.dispatch(fetchSessions({ programId }))
            return responseData?.data?.sessions || [];
        } catch (error: any) {
            console.error('Error:', error.message);
            return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
        }
    }
);

/**
 * Axios API request to delete a session
 */
export const deleteSession = createAsyncThunk('sessions/storeSessions',
    async ({ sessionId, programId }: IDeleteSession, thunkAPI) => {
        try {
            const accessToken = JSON.parse(localStorage.getItem('loginData') as string)?.accessToken;

            if (!accessToken) {
                console.error('Access token not found in the response.');
                return {}; // or any default value if needed
            }
            const response = await axios.delete(`${BASE_URL}/api/v1/sessions/${sessionId}`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    // Add other headers if needed
                },
            });
            const responseData = response?.data
            if (response.status !== 200 && response.statusText !== 'OK') {
                throw new Error(`Request failed with status ${response?.data?.message}`);
            }
            thunkAPI.dispatch(fetchSessions({ programId: programId?.toString() }))
            return responseData?.data?.sessions || [];
        } catch (error: any) {
            console.error('Error:', error.message);
            return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
        }
    }
);

const programSessionsSlice = createSlice({
    name: "programSessions",
    initialState,
    reducers: {
        resetState: (state) => initialState
    },
    extraReducers: (builder) => {
        builder.addCase(fetchSessions.pending, (state) => {
            state.loading = true;
        })
            .addCase(fetchSessions.fulfilled, (state, action) => {
                state.loading = false;
                state.programSessions = action.payload;
            })
            .addCase(fetchSessions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message ?? 'An error occurred';
            });
    },
});

export const { resetState } = programSessionsSlice.actions;
export default programSessionsSlice.reducer;